<template>
  <TabPanel
    :unmount="false"
    v-slot="{ selected }"
  >
    <slot v-if="shouldRender(selected)" />
  </TabPanel>
</template>

<script setup lang="ts">
  import { ref, provide } from 'vue'
  import { TabPanel } from '@headlessui/vue'

  defineOptions({ name: 'UiTab' })

  interface Props {
    title?: string
    icon?: string
    active?: boolean
    lazy?: boolean
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '',
    icon: '',
    active: false,
    lazy: false,
    disabled: false
  })

  // Lazy semantics matching b-tab: don't mount slot content until first
  // selected, then keep it mounted across switches so internal state survives.
  // The latch only flips false→true, so the in-render mutation is stable.
  const everSelected = ref(props.active)

  // Expose `selected` reactively to descendants so they can decide whether
  // to teleport content into the tab strip's actions area. PagedTable uses
  // this so only the active tab's search bar is visible up there.
  const selectedRef = ref(props.active)
  provide('uiTabSelected', selectedRef)

  function shouldRender (selected: boolean): boolean {
    selectedRef.value = selected
    if (selected && !everSelected.value) everSelected.value = true
    return !props.lazy || everSelected.value
  }

  // The title/active/disabled props are read by UiTabs via slot VNode
  // introspection; UiTab itself only needs to render the panel.
</script>
